'use client';

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import Cookies from 'js-cookie';
import { useGoogleLogin, useGoogleOneTapLogin } from '@react-oauth/google';
import { AdblockDetector } from 'adblock-detector';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertTriangle, Eye, EyeOff } from 'lucide-react';
import AuthCard from './AuthCard';
import GoogleIcon from '../../public/Icons/GoogleIcon.svg';
import { Alert as ShadcnAlert, AlertDescription, AlertTitle } from '../components/ui/alert';
import AuthLayout from './AuthLayout';
import ModalCloseButton from '../components/generic/ModalCloseButton';
import {
  getJsCookieTokenOptions,
  getReactCookieTokenOptions,
} from '../../../../apps/frontend/src/utils/tokenCookieOptions';

const useQuery = () => new URLSearchParams(useLocation().search);

const Login = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const showLoginHelpTestMode = query.get('show_login_help') === '1';
  const [cookies, setCookie] = useCookies();
  const [loading, setLoading] = useState(false);
  const [loginStatus, setLoginStatus] = useState(0);
  const [passVisible, setPassVisible] = useState(false);
  const [isAdBlocked, setIsAdBlocked] = useState(false);
  const [tokenCookie, setTokenCookie] = useState(Cookies.get('token') || null);
  const [showAlert, setShowAlert] = useState(showLoginHelpTestMode);
  const [alertMessage, setAlertMessage] = useState('');
  const [showLoginHelpModal, setShowLoginHelpModal] = useState(showLoginHelpTestMode);

  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({});

  const adbDetector = new AdblockDetector();
  const detected = adbDetector.detect();

  const redirect_url = query.get('redirect_url') || localStorage.getItem('redirect_url');

  if (redirect_url?.length > 0) {
    localStorage.setItem('redirect_url', redirect_url);
  }

  useEffect(() => {
    const interval = setInterval(() => {
      const newTokenCookie = Cookies.get('token');
      if (newTokenCookie !== undefined && tokenCookie === null) {
        setTokenCookie(newTokenCookie);
        clearInterval(interval);
      }
    }, 1000);

    if (!navigator.cookieEnabled || detected) {
      setIsAdBlocked(true);
    }

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const checkLoginStatus = () => {
      if (sessionStorage.getItem('token')) {
        Cookies.set(
          'token',
          sessionStorage.getItem('token'),
          getJsCookieTokenOptions({ sameSite: 'None', secure: true }),
        );
      }
      if (cookies['token'] || sessionStorage.getItem('token')) {
        if (redirect_url) {
          navigate(redirect_url, { replace: true });
          localStorage.removeItem('redirect_url');
        } else {
          navigate('/v2/home');
        }
      }
    };

    if (loginStatus === 0) {
      checkLoginStatus();
    }
  }, [loginStatus]);

  const validateEmail = (email) => {
    return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
      email,
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'email') {
      if (value.trim() === '') {
        setErrors((prev) => ({ ...prev, email: undefined }));
      } else if (!validateEmail(value)) {
        setErrors((prev) => ({ ...prev, email: 'Invalid Email' }));
      } else {
        setErrors((prev) => ({ ...prev, email: undefined }));
      }
    } else if (name === 'password') {
      if (value.trim() === '') {
        setErrors((prev) => ({ ...prev, password: undefined }));
      } else if (value.length < 6) {
        setErrors((prev) => ({ ...prev, password: 'Password must be at least 6 characters' }));
      } else {
        setErrors((prev) => ({ ...prev, password: undefined }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'This field is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      newErrors.password = 'This field is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePostGoogleLogin = ({ token, user }) => {
    setLoginStatus(1);
    setCookie('token', token, getReactCookieTokenOptions({ sameSite: 'lax', secure: true }));
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('userProfile', JSON.stringify(user));
    window?.webengage?.user?.login(user.email);

    if (user?.isOnboardingV2Done && redirect_url) {
      navigate(redirect_url, { replace: true });
      localStorage.removeItem('redirect_url');
    } else {
      navigate('/v2/home');
    }
  };

  const handleGoogleOneTapSuccess = async (credentialResponse) => {
    try {
      const login = await axios.post(`/api/auth/user/google-login/v2`, {
        credential: credentialResponse.credential,
      });
      if (login?.status === 200) {
        handlePostGoogleLogin(login.data.data);
      } else {
        setLoginStatus(2);
      }
    } catch (err) {
      console.error('Google One Tap login error', err);
    }
  };

  useGoogleOneTapLogin({
    onSuccess: handleGoogleOneTapSuccess,
    onError: () => console.error('Google One Tap Login Failed'),
    disabled: isAdBlocked,
    use_fedcm_for_prompt: false,
    cancel_on_tap_outside: true,
    promptMomentNotification: (notification) => {
      // Google's GSI script leaves a fixed, high z-index
      // #credential_picker_container in the DOM reserved for the prompt
      // even when it isn't actually displayed (common on Safari/iOS due to
      // third-party cookie restrictions). That invisible container can
      // overlap and block clicks on page content underneath it, so tear it
      // down explicitly whenever the prompt didn't render.
      if (notification.isNotDisplayed()) {
        console.warn('One Tap not displayed:', notification.getNotDisplayedReason());
        window.google?.accounts?.id?.cancel();
      } else if (notification.isSkippedMoment()) {
        console.warn('One Tap skipped:', notification.getSkippedReason());
        window.google?.accounts?.id?.cancel();
      }
    },
  });

  const handleGoogleAuthCode = async (tokenResponse) => {
    try {
      const login = await axios.post(`/api/auth/user/google-login/v2`, {
        auth_code: tokenResponse.code,
      });
      if (login?.status === 200) {
        handlePostGoogleLogin(login.data.data);
      } else {
        setLoginStatus(2);
      }
    } catch (err) {
      console.error('Google login error', err);
      alert('Google login failed');
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleAuthCode,
    onError: () => alert('Google login failed'),
    flow: 'auth-code',
  });

  const onSubmit = (e) => {
    e.preventDefault();
    const trimmedEmail = formData.email.trim().toLowerCase();
    const trimmedPassword = formData.password.trim();

    setFormData((prev) => ({
      ...prev,
      email: trimmedEmail,
      password: trimmedPassword,
    }));

    if (!validateForm()) return;

    setLoading(true);
    setShowAlert(false);
    setAlertMessage('');

    axios
      .post(`/api/auth/user/loginManual`, {
        email: trimmedEmail,
        password: trimmedPassword,
      })
      .then((res) => {
        if (res?.status == 200) {
          const { token, user } = res.data?.data;
          setLoginStatus(1);
          setCookie('token', token, getReactCookieTokenOptions());
          localStorage.setItem('userProfile', JSON.stringify(user));
          window?.webengage?.user?.login(trimmedEmail);

          if (redirect_url) {
            window.location.href = redirect_url;
            localStorage.removeItem('redirect_url');
          } else {
            window.location.href = '/v2/home';
          }
        } else {
          setLoading(false);
          setAlertMessage('Could not login. Please check your password and ensure that your email is verified');
          setShowAlert(true);
        }
      })
      .catch((err) => {
        setLoading(false);
        if (err?.response?.data?.message.includes('password')) {
          setErrors(() => ({ password: 'Incorrect Password' }));
          setAlertMessage('');
        } else {
          setAlertMessage(err?.response?.data?.message);
          setShowAlert(true);
        }
      });
  };

  if (isAdBlocked) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-[#ffffff] p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <h2 className="text-xl font-semibold flex flex-row items-center justify-center gap-2 mb-4 text-[#f3164e]">
            <AlertTriangle size={20} className="text-[#f3164e]" /> AdBlocker Detected
          </h2>
          <p className="text-[#1d2933] mb-4">
            You might not be able to log in as either ad-blocker is installed or cookies in your browser are disabled.
          </p>
          <p className="text-[#60748f] mb-6">
            Please disable the ad-blocking extensions and allow/enable cookies to continue.
          </p>
          <Button onClick={() => window.location.reload()} className="px-4 py-2 rounded">
            Refresh Page
          </Button>
        </div>
      </div>
    );
  }

  const isValid = formData.email && formData.password && validateEmail(formData.email);
  const hasLoginFailedReason = typeof alertMessage === 'string' && alertMessage.trim().length > 0;

  const handleForgotPassword = () => {
    navigate('/auth/reset/request');
  };

  return (
    <AuthLayout>
      <AuthCard title="Login to Test Series" subtitle="Welcome back! Please login to continue">
        <form onSubmit={onSubmit} className="space-y-5">
          <Button
            type="button"
            variant="outline"
            onClick={() => googleLogin()}
            disabled={isAdBlocked}
            className="w-full h-[40px] !bg-white text-black rounded-full hover:text-black hover:scale-[1.02] duration-300"
          >
            <img src={GoogleIcon || '/placeholder.svg'} className="w-6 h-auto" />
            Login with Google
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t" style={{ borderColor: '#d9e6f2' }}></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span
                className="px-2"
                style={{
                  backgroundColor: '#ffffff',
                  color: '#60748f',
                }}
              >
                or
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" style={{ color: '#1d2933' }}>
              Email Address
            </Label>
            <Input
              ref={emailRef}
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Email address"
              className={`h-[40px] rounded-full ${errors.email ? 'border-[#f3164e]' : ''}`}
              style={{
                backgroundColor: '#ffffff',
                border: errors.email ? '1px solid #f3164e' : '1px solid #d9e6f2',
                color: '#1d2933',
              }}
            />
            {errors.email && (
              <div className="flex items-center gap-1 text-sm" style={{ color: '#f3164e' }}>
                <AlertTriangle size={12} className="text-[#f3164e]" />
                {errors.email}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" style={{ color: '#1d2933' }}>
              Password
            </Label>
            <div className="relative">
              <Input
                ref={passwordRef}
                id="password"
                name="password"
                type={passVisible ? 'text' : 'password'}
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Password"
                className={`h-[40px] rounded-full pr-12 ${errors.password ? 'border-[#f3164e]' : ''}`}
                style={{
                  backgroundColor: '#ffffff',
                  border: errors.password ? '1px solid #f3164e' : '1px solid #d9e6f2',
                  color: '#1d2933',
                }}
              />
              <button
                type="button"
                onClick={() => setPassVisible(!passVisible)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 cursor-pointer"
                style={{ color: '#60748f' }}
              >
                {passVisible ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.password && (
              <div className="flex items-center gap-1 text-sm" style={{ color: '#f3164e' }}>
                <AlertTriangle size={12} className="text-[#f3164e]" />
                {errors.password}
              </div>
            )}
          </div>

          <div className="text-right">
            <button
              type="button"
              onClick={handleForgotPassword}
              className="text-sm hover:underline cursor-pointer"
              style={{ color: '#60748f' }}
            >
              Forgot password?
            </button>
          </div>
          {showAlert && (
            <div className="flex flex-col items-center justify-center text-sm text-center" style={{ color: '#f3164e' }}>
              <div className="flex items-center gap-2 justify-center">
                <AlertTriangle size={18} className="text-[#f3164e]" />
                <span>Login Failed{hasLoginFailedReason ? ` : ${alertMessage}` : '.'}</span>
              </div>
              {!hasLoginFailedReason && (
                <button
                  type="button"
                  onClick={() => setShowLoginHelpModal(true)}
                  className="mt-1 underline cursor-pointer text-[var(--q3-accent-normal)]"
                >
                  Check why?
                </button>
              )}
            </div>
          )}
          {errors.general && (
            <div className="text-sm text-center" style={{ color: '#f3164e' }}>
              {errors.general}
            </div>
          )}

          <Button
            type="submit"
            disabled={!isValid || loading}
            className="w-full h-[40px] text-base bg-[#4338ca] rounded-full hover:bg-[#4338ca)]/50 hover:scale-[1.02] duration-300"
            style={{
              backgroundColor: '#4338ca)',
              color: '#ffffff',
              border: 'none',
            }}
          >
            {loading ? 'Logging in...' : 'Login with Email'}
          </Button>
        </form>
        <p className="text-sm text-[var(--q3-neutral-light)] mt-5">
          By signing up, you agree to our{' '}
          <a
            href="https://mathongo.com/privacy-policy"
            target="_blank"
            rel="noreferrer"
            className="text-[var(--q3-accent-normal)] underline"
          >
            Privacy Policy
          </a>{' '}
          and{' '}
          <a
            href="https://www.mathongo.com/terms-conditions/"
            target="_blank"
            rel="noreferrer"
            className="text-[var(--q3-accent-normal)] underline"
          >
            Terms & Conditions
          </a>
          .
        </p>
      </AuthCard>

      {showLoginHelpModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{
            background: 'rgba(16, 24, 40, 0.70)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Login troubleshooting help"
        >
          <div className="relative bg-[var(--q3-surface-default)] rounded-lg p-6 w-full max-w-lg">
            <ModalCloseButton onClick={() => setShowLoginHelpModal(false)} />

            <h3 className="text-lg font-semibold text-[var(--q3-neutral-default)] mb-3">Trouble logging in?</h3>
            <p className="text-sm text-[var(--q3-neutral-light)] mb-5">
              These are the most common causes and quick fixes.
            </p>

            <div className="text-sm text-[var(--q3-neutral-default)]">
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="mt-2 h-2 w-2 rounded-full bg-[var(--q3-accent-normal)] shrink-0" />
                  <div>
                    <div className="font-semibold">Network Provider Issue</div>
                    <div className="text-[var(--q3-neutral-light)]">
                      Try switching your network (WiFi ↔ mobile data) or refresh the page.
                    </div>
                  </div>
                </li>

                <li className="flex items-start gap-3">
                  <div className="mt-2 h-2 w-2 rounded-full bg-[var(--q3-accent-normal)] shrink-0" />
                  <div>
                    <div className="font-semibold">Network settings issue</div>
                    <div className="text-[var(--q3-neutral-light)]">
                      Try changing your DNS to 1.1.1.1 or 8.8.8.8.{' '}
                      <a
                        href="https://www.google.com/search?q=how+to+change+my+dns+to+1.1.1.1"
                        target="_blank"
                        rel="noreferrer"
                        className="text-[var(--q3-accent-normal)] underline"
                      >
                        Check how?
                      </a>
                    </div>
                  </div>
                </li>

                <li className="flex items-start gap-3">
                  <div className="mt-2 h-2 w-2 rounded-full bg-[var(--q3-accent-normal)] shrink-0" />
                  <div>
                    <div className="font-semibold">Using VPN</div>
                    <div className="text-[var(--q3-neutral-light)]">
                      If you are using a VPN, turn it off and try again.
                    </div>
                  </div>
                </li>

                <li className="flex items-start gap-3">
                  <div className="mt-2 h-2 w-2 rounded-full bg-[var(--q3-accent-normal)] shrink-0" />
                  <div>
                    <div className="font-semibold">Browser Issue</div>
                    <div className="text-[var(--q3-neutral-light)]">
                      Turn off any ad blockers or try in a different browser.
                    </div>
                  </div>
                </li>

                <li className="flex items-start gap-3">
                  <div className="mt-2 h-2 w-2 rounded-full bg-[var(--q3-accent-normal)] shrink-0" />
                  <div>
                    <div className="font-semibold">Security apps blocking connection</div>
                    <div className="text-[var(--q3-neutral-light)]">
                      Antivirus or security apps may block the site. Try disabling them temporarily.
                    </div>
                  </div>
                </li>
              </ul>

              <p className="mt-4 text-[var(--q3-neutral-light)]">
                If you&apos;re still facing issues, please contact us at{' '}
                <a
                  href="mailto:support@mathongo.com?subject=login%20issue"
                  className="text-[var(--q3-accent-normal)] underline"
                >
                  support@mathongo.com
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      )}
    </AuthLayout>
  );
};

export default Login;
